import { AgencyActivityTypesField, TableInlineColumn, TableInlines } from 'components';
import { AttachmentField } from 'modules/attachments/AttachmentField';
import {
  TextField,
  DateRangePickerField,
  IntField,
  SelectField,
  DecimalField,
  useForm,
  useOnFieldChange,
} from '@ff-it/form';
import { agreementKindOptions } from 'configuration';
import { ReactElement } from 'react';
import { required } from 'utilities';
import { ProductField } from '../products/ProductField';

import { OutletOrServiceCategoryField } from '../outletOrServiceCategory/OutletOrServiceCategoryField';
import { ProvidedOutletOrServiceCategory, ProvidedProduct, ProvidedVendor } from './types';
import { VendorField } from '../vendor/VendorField';

const vendorInitial = {
  venodr: null,
  agency_discount: null,
};

const vendorColumns: TableInlineColumn<ProvidedVendor>[] = [
  {
    key: 'vendor',
    content: 'Provided vendor',
    render: ({ name }) => <VendorField name={`${name}.vendor`} className="mb-0" required validate={required} />,
  },
  {
    width: '150px',
    key: 'agency_discount',
    content: 'Agency discount %',
    render: ({ name }) => <DecimalField name={`${name}.agency_discount`} className="mb-0" precision={2} />,
  },
];

const outeltInitial = {
  outlet_or_service_category: null,
  type: null,
  agency_discount: null,
};

const outletColumns: TableInlineColumn<ProvidedOutletOrServiceCategory>[] = [
  {
    width: '150px',
    content: 'Type',
    key: 'type',
    render: ({ name }) => (
      <AgencyActivityTypesField name={`${name}.type`} className="mb-0" required validate={required} />
    ),
  },
  {
    key: 'outlet_or_service_category',
    content: 'Provided outlet or service category',
    render: ({ name }) => (
      <OutletOrServiceCategoryField
        name={`${name}.outlet_or_service_category`}
        className="mb-0"
        required
        validate={required}
      />
    ),
  },

  {
    width: '150px',
    key: 'agency_discount',
    content: 'Agency discount %',
    render: ({ name }) => <DecimalField name={`${name}.agency_discount`} className="mb-0" precision={2} />,
  },
];

const productInitial = {
  type: null,
  product: null,
  agency_discount: null,
};

function TypeProductField({ name }: { name: string }) {
  const form = useForm();
  const type = useOnFieldChange(`${name}.type`, () => {
    form.change(`${name}.product`, null);
  });

  return (
    <ProductField name={`${name}.product`} type={type} disabled={!type} className="mb-0" required validate={required} />
  );
}

const productColumns: TableInlineColumn<ProvidedProduct>[] = [
  {
    width: '150px',
    content: 'Type',
    key: 'type',
    render: ({ name }) => (
      <AgencyActivityTypesField name={`${name}.type`} className="mb-0" required validate={required} />
    ),
  },
  {
    key: 'product',
    content: 'Provided product',
    render: ({ name }) => <TypeProductField name={name} />,
  },

  {
    width: '150px',
    key: 'agency_discount',
    content: 'Agency discount %',
    render: ({ name }) => <DecimalField name={`${name}.agency_discount`} className="mb-0" precision={2} />,
  },
];

export function Form(): ReactElement {
  return (
    <div className="container px-0 ml-0">
      <div className="form-row">
        <TextField name="number" label="Number" className="col-md-3" help="Leave blank to assign" />
        <SelectField label="Kind" name="kind" options={agreementKindOptions} simple required className="col-md-3" />
        <DateRangePickerField label="Period" fromName="date_from" toName="date_to" className="col-md-3" required />
        <IntField label="Invoice payment days" name="invoice_payment_days" className="col-md-3" />
      </div>
      <div className="form-row">
        <div className="col-md-6">
          <AttachmentField name="attachments" label="Attachments" />
        </div>
        <div className="col-md-6"></div>
      </div>

      <div className="form-row">
        <TextField
          name="terms"
          className="col-6"
          label="Terms and conditions"
          multiline
          help="General terms and conditions"
        />
        <TextField name="notes" className="col-6" label="Notes" multiline help="Private notes for internal use" />
      </div>
      <div className="form-row">
        <DecimalField name="agency_discount" label="Agency discount %" className="col-md-2" precision={2} />
      </div>
      <TableInlines name="provided_vendors" initialValues={vendorInitial} columns={vendorColumns} sortable={false} />
      <TableInlines
        name="provided_outlets_or_service_categories"
        initialValues={outeltInitial}
        columns={outletColumns}
        sortable={false}
      />
      <TableInlines name="provided_products" initialValues={productInitial} columns={productColumns} sortable={false} />
    </div>
  );
}
